import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Archive, Clock3, History, RotateCcw, Tag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { InterimTagEditorV53 } from "../components/InterimTagEditorV53";
import { ProjectDetailPageV5 } from "./ProjectDetailPageV5";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;



const COMPLETED_STATUSES = new Set(["graphics_complete", "graphics_completed", "approved"]);

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const ProjectHistory = ({ projects, currentProjectId }) => {
  if (!projects?.length) return null;

  return (
    <section className="mt-6 rounded-xl border border-border bg-card p-6">
      <div className="mb-5 flex items-center gap-2">
        <History size={19} />
        <div>
          <h2 className="text-lg font-semibold">Project History</h2>
          <p className="text-sm text-muted-foreground">Original project and every revision in this project family.</p>
        </div>
      </div>

      <div className="space-y-3">
        {projects.map((item) => {
          const revisionNumber = Number(item.revision_number || 0);
          const isCurrent = item.id === currentProjectId;
          const isArchived = item.is_archived === true;
          const isCompleted = COMPLETED_STATUSES.has(item.workflow_phase || item.status);
          const stateLabel = isArchived ? "Archived" : isCompleted ? "Completed" : "Active";

          return (
            <div
              key={item.id}
              className={`flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center md:justify-between ${isCurrent ? "border-primary" : "border-border"}`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/projects/${item.id}`} className="font-medium hover:underline">
                    {revisionNumber === 0 ? "Original Project" : `Revision ${revisionNumber}`}
                  </Link>
                  <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-xs">
                    {stateLabel}
                  </span>
                  {isCurrent && (
                    <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      Current View
                    </span>
                  )}
                </div>
                <p className="mt-1 truncate text-sm">{item.name}</p>
                {item.revision_reason && (
                  <p className="mt-1 text-sm text-muted-foreground">Reason: {item.revision_reason}</p>
                )}
              </div>
              <div className="shrink-0 text-xs text-muted-foreground">
                {isCompleted ? `Completed ${formatDateTime(item.completed_at)}` : `Created ${formatDateTime(item.created_at)}`}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const ProjectDetailPageV6 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canChangeProjectStatus, canCreateProject } = useAuth();
  const [tag, setTag] = useState("");
  const [project, setProject] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creatingRevision, setCreatingRevision] = useState(false);
  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");
  const [duplicateName, setDuplicateName] = useState("");
  const [copyReferences, setCopyReferences] = useState({ artwork_po: false, gamer_po: false, customer_po: false, po_number: false });
  const [lifecycleSchedule, setLifecycleSchedule] = useState(null);
  const [notificationRetryBusy, setNotificationRetryBusy] = useState(false);
  const [returnNotification, setReturnNotification] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const loadLifecycle = async () => {
    try {
      const [projectResponse, familyResponse] = await Promise.all([
        axios.get(`${API}/workflow/projects/${id}`),
        axios.get(`${API}/projects/${id}/family`),
      ]);
      setProject(projectResponse.data || null);
      try {
        const result = await axios.get(`${API}/projects/${id}/return-notification`);
        setReturnNotification(result.data);
      } catch {
        setReturnNotification({ available: false, status: "unavailable", can_retry: false });
      }
      setTag(projectResponse.data?.sub_status || "");
      setHistory(familyResponse.data?.projects || []);
      try { const preview = await axios.get(`${API}/projects/${id}/lifecycle`); setLifecycleSchedule(preview.data); }
      catch { setLifecycleSchedule(null); }
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    setProject(null);
    setReturnNotification(null);
    setHistory([]);
    setTag("");
    setAction("");
    setReason("");
    loadLifecycle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const historical = project?.is_archived === true || COMPLETED_STATUSES.has(project?.workflow_phase || project?.status);

  const updateTag = async (value) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/workflow/projects/${id}/status-tag`, { tag: value });
      setTag(response.data?.sub_status || "");
      setProject(response.data || project);
      toast.success(value ? `Status tag set to ${value}` : "Status tag cleared");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status tag");
    } finally {
      setSaving(false);
    }
  };

  const startProduction = async () => {
    setActionBusy(true);
    try {
      await axios.put(`${API}/workflow/projects/${id}/phase`, { phase: "in_production" });
      await loadLifecycle();
      setReloadKey((value) => value + 1);
      toast.success("Moved to In Production; automatic archive is due in seven days");
      window.dispatchEvent(new Event("inksight:kanban-refresh-needed"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start production");
    } finally {
      setActionBusy(false);
    }
  };

  const submitAction = async (event) => {
    event.preventDefault();
    if (action === "revert" && !reason.trim()) return;
    setActionBusy(true);
    try {
      if (action === "revert") {
        const { data } = await axios.post(`${API}/projects/${id}/revert-to-intake`, { reason: reason.trim() });
        const status = data.notification?.status;
        if (status === "sent") toast.success("Returned to Intake Graphics; salesperson notified");
        else toast.warning("Returned to Intake Graphics. Salesperson notification was not sent; check activity history.");
        setReason("");
        await loadLifecycle();
        setReloadKey((value) => value + 1);
        window.dispatchEvent(new Event("inksight:kanban-refresh-needed"));
      } else {
        const { data } = await axios.post(`${API}/projects/${id}/duplicate-with-options`, {
          ...(duplicateName.trim() ? { name: duplicateName.trim() } : {}),
          copy_artwork_po: copyReferences.artwork_po,
          copy_gamer_po: copyReferences.gamer_po,
          copy_customer_po: copyReferences.customer_po,
          copy_po_number: copyReferences.po_number,
        });
        toast.success("Project duplicated with fresh workflow and no files");
        navigate(`/projects/${data.id}`);
      }
      setAction("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Project action failed");
    } finally {
      setActionBusy(false);
    }
  };

  const createRevision = async () => {
    const reason = window.prompt(`Revision reason for ${project?.name || "this project"}:`);
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error("Revision reason is required");
      return;
    }

    setCreatingRevision(true);
    try {
      const response = await axios.post(`${API}/projects/${id}/revisions`, {
        revision_reason: reason.trim(),
      });
      toast.success(`Revision ${response.data.revision_number} created`);
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create revision");
    } finally {
      setCreatingRevision(false);
    }
  };

  const statusStrip = (
    <div className="mt-2 inline-flex max-w-full flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5">
      <div className="flex items-center gap-1.5">
        <Tag size={14} className="text-muted-foreground" />
        <span className="text-xs font-medium">Current Status</span>
      </div>
      <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] font-semibold">
        {project?.workflow_phase_label || "Loading..."}
      </span>
      <span className="text-[11px] text-muted-foreground">Interim Tag</span>
      {canChangeProjectStatus && !historical ? (
        <InterimTagEditorV53 value={tag} disabled={saving} onChange={updateTag} />
      ) : (
        <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px]">
          {tag || "No tag"}
        </span>
      )}
    </div>
  );

  return (
    <div>
      {project && !historical && (canChangeProjectStatus || canCreateProject) && (
        <section className="mb-4 rounded-xl border border-border bg-card p-4" aria-label="Project actions">
          <div className="flex flex-wrap gap-2">
            {canChangeProjectStatus && project.workflow_phase !== "awaiting_graphics_approval" && (
              <button type="button" disabled={actionBusy} onClick={() => setAction("revert")}
                className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm">Revert to Intake</button>
            )}
            {canCreateProject && (
              <button type="button" disabled={actionBusy} onClick={() => { setAction("duplicate"); setDuplicateName(`${project.name || "Project"} — Copy`); }}
                className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm">Duplicate Project / Open Label</button>
            )}
          </div>
          {action && (
            <form onSubmit={submitAction} className="mt-3 space-y-3">
              {action === "revert" ? (
                <label className="block text-sm">Reason for returning to Intake Graphics
                  <textarea required maxLength={2000} value={reason} disabled={actionBusy}
                    onChange={(event) => setReason(event.target.value)}
                    className="mt-1 block w-full rounded border border-border bg-background p-2" />
                </label>
              ) : (
                <>
                  <label className="block text-sm">New project name
                    <input required maxLength={200} value={duplicateName} disabled={actionBusy}
                      onChange={(event) => setDuplicateName(event.target.value)}
                      className="mt-1 block w-full rounded border border-border bg-background p-2" />
                  </label>
                  <p className="text-sm text-muted-foreground">Copies project metadata only. Files, approvals, completed workflow and previous activity are excluded.</p>
                  <p className="text-xs text-muted-foreground">PO references are cleared unless deliberately selected. Verify the new order before proceeding.</p>
                  <div className="grid gap-2 md:grid-cols-2">{[["artwork_po", "Artwork PO"], ["gamer_po", "Gamer PO"], ["customer_po", "Customer PO"], ["po_number", "PO Number"]].map(([field, label]) => (
                    <label key={field} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={copyReferences[field]} disabled={actionBusy} onChange={e => setCopyReferences(v => ({ ...v, [field]: e.target.checked }))} />Carry over {label}</label>
                  ))}</div>
                </>
              )}
              <div className="flex gap-2">
                <button type="submit" disabled={actionBusy || (action === "revert" && !reason.trim()) || (action === "duplicate" && !duplicateName.trim())}
                  className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50">{actionBusy ? "Working…" : action === "revert" ? "Return & Notify Salesperson" : "Create Duplicate"}</button>
                <button type="button" disabled={actionBusy} onClick={() => setAction("")} className="rounded bg-secondary px-3 py-2 text-sm">Cancel</button>
              </div>
            </form>
          )}
        </section>
      )}
      {lifecycleSchedule?.archives_on && (
        <p className="mb-4 rounded-lg border border-border bg-card p-4 text-sm">
          Archives on: <strong>{formatDateTime(lifecycleSchedule.archives_on)}</strong> ({lifecycleSchedule.retention_days}-day phase rule)
        </p>
      )}
      {lifecycleSchedule?.requires_date_review && (
        <p className="mb-4 rounded-lg border border-amber-700 bg-card p-4 text-sm">Archive date unavailable; review lifecycle timestamps.</p>
      )}
      {returnNotification?.available && (
        <section className="mb-4 rounded-lg border border-border bg-card p-4 text-sm" aria-label="Return notification status">
          <p className="font-medium">Return notification: {({ sent: "Sent to mail provider", failed: "Delivery attempt failed", no_recipient: "No recipient found", sending: "Delivery in progress or uncertain" })[returnNotification.status] || "Status requires review"}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Attempts: {returnNotification.attempts} of {returnNotification.max_attempts}
            {returnNotification.updated_at ? ` · Last attempt: ${formatDateTime(returnNotification.updated_at)}` : ""}
          </p>
          {returnNotification.requires_review && <p className="mt-2 text-amber-700">The send result is uncertain. Review email logs before attempting another send.</p>}
          {returnNotification.can_retry && canChangeProjectStatus && (
            <button type="button" className="mt-3 rounded-lg border border-border px-4 py-2 text-sm" disabled={notificationRetryBusy} onClick={async () => {
              setNotificationRetryBusy(true);
              try {
                const { data } = await axios.post(`${API}/projects/${id}/return-notification/retry`);
                if (data.notification?.status === "sent") toast.success("Notification accepted by mail provider");
                else toast.warning("Notification retry unsuccessful");
                await loadLifecycle();
              } catch (err) { toast.error(err.response?.data?.detail || "Could not retry notification"); }
              finally { setNotificationRetryBusy(false); }
            }}>{notificationRetryBusy ? "Retrying…" : "Retry return notification (email only)"}</button>
          )}
        </section>
      )}
      {project?.workflow_phase === "in_production" && (
        <p className="mb-4 rounded-lg border border-border bg-card p-4 text-sm">
          In Production since {formatDateTime(project.production_started_at)}. Automatically archives seven days after production starts.
        </p>
      )}
      {historical && project && (
        <div className="mb-4 rounded-xl border border-border bg-card px-5 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              {project.is_archived ? <Archive size={21} className="mt-0.5 text-muted-foreground" /> : <Clock3 size={21} className="mt-0.5 text-muted-foreground" />}
              <div>
                <p className="font-semibold">{project.is_archived ? "Archived Historical Project" : "Completed Project"}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Completed {formatDateTime(project.completed_at)}{project.completed_by ? ` by ${project.completed_by}` : ""}.
                  {project.is_archived && ` Archived ${formatDateTime(project.archived_at)}.`}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {project.is_archived
                    ? "Create a revision to continue work as a new active project."
                    : "Graphics work is complete. Start production when ready, or create a revision for further graphics work."}
                </p>
              </div>
            </div>
            {canChangeProjectStatus && !project.is_archived && (
              <button type="button" disabled={actionBusy} onClick={startProduction}
                className="rounded-lg bg-primary px-4 py-2.5 font-medium text-primary-foreground disabled:opacity-50">
                {actionBusy ? "Starting…" : "Start Production"}
              </button>
            )}
            {canCreateProject && (
              <button
                type="button"
                onClick={createRevision}
                disabled={creatingRevision}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 font-medium text-primary-foreground disabled:opacity-50"
              >
                <RotateCcw size={17} />
                {creatingRevision ? "Creating Revision..." : "Create Revision"}
              </button>
            )}
          </div>
        </div>
      )}

      <ProjectDetailPageV5
        key={`${id}-${reloadKey}`}
        onProjectChanged={loadLifecycle}
        headerAddon={statusStrip}
        readOnlyHistorical={historical}
      />
      <ProjectHistory projects={history} currentProjectId={id} />
    </div>
  );
};
